// src/components/practice/writing/AskTeacherPanel.jsx
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';
import { GraduationCap, Send, CheckCircle2 } from 'lucide-react';
import { auth, db, functions } from '../../../firebase';
import { getActivePerks } from '../../../services/planPerksService';
import { useLocalizedNavigate } from '../../../hooks/useLocalizedNavigate';

const toDate = (value) => (value?.toDate ? value.toDate() : value ? new Date(value) : null);

/**
 * "Ask my teacher" under an AI-corrected text. Premium students can send up to
 * 10 texts for a personal correction; the request is created by the
 * requestTeacherCorrection function (which enforces the allowance) and the
 * teacher answers in the admin dashboard. Everyone else sees one quiet line
 * pointing to Premium.
 */
const AskTeacherPanel = ({ submissionId }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [state, setState] = useState({ status: 'loading' });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (!userId || !submissionId) {
      setState({ status: 'hidden' });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [perks, mine] = await Promise.all([
          getActivePerks(userId),
          getDocs(query(collection(db, 'teacherCorrections'), where('userId', '==', userId))),
        ]);
        if (cancelled) return;
        const requests = mine.docs.map((d) => ({ id: d.id, ...d.data() }));
        const existing = requests.find((r) => r.id === submissionId);
        if (existing) {
          setState({ status: existing.status === 'done' ? 'done' : 'requested', request: existing });
          return;
        }
        if (perks.teacherCorrections <= 0) {
          setState({ status: 'upsell' });
          return;
        }
        const used = requests.filter((r) => {
          const at = toDate(r.requestedAt);
          return !perks.since || !at || at >= perks.since;
        }).length;
        setState({
          status: 'available',
          allowance: perks.teacherCorrections,
          remaining: Math.max(0, perks.teacherCorrections - used),
        });
      } catch (err) {
        console.error('Could not load teacher corrections:', err);
        if (!cancelled) setState({ status: 'hidden' });
      }
    })();
    return () => { cancelled = true; };
  }, [submissionId]);

  const ask = async () => {
    setSending(true);
    setError(null);
    try {
      await httpsCallable(functions, 'requestTeacherCorrection')({ submissionId });
      setState({ status: 'requested' });
    } catch (err) {
      setError(err?.code === 'functions/resource-exhausted'
        ? t('teacherCorrection.noneLeft')
        : t('teacherCorrection.error'));
    } finally {
      setSending(false);
    }
  };

  if (state.status === 'loading' || state.status === 'hidden') return null;

  if (state.status === 'upsell') {
    return (
      <p className="mt-3 text-xs text-black/55">
        {t('teacherCorrection.upsell')}{' '}
        <button
          type="button"
          onClick={() => navigate('/pricing')}
          className="font-bold text-black underline underline-offset-2 hover:text-[#3A86FF] transition-colors"
        >
          {t('teacherCorrection.upsellLink')}
        </button>
      </p>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border-2 border-black bg-white p-4 animate-fade-in-up">
      <div className="flex items-center gap-2 mb-2">
        <GraduationCap className="w-4 h-4 text-[#006D77] flex-shrink-0" />
        <span className="font-space font-black text-sm text-black">{t('teacherCorrection.title')}</span>
        {state.status === 'available' && (
          <span className="ml-auto text-[11px] font-semibold text-black/50">
            {t('teacherCorrection.remaining', { remaining: state.remaining, allowance: state.allowance })}
          </span>
        )}
      </div>

      {state.status === 'available' && (
        <>
          <p className="text-sm text-black/65 mb-3">{t('teacherCorrection.askHint')}</p>
          <button
            type="button"
            onClick={ask}
            disabled={sending || state.remaining <= 0}
            className="w-full min-h-[48px] inline-flex items-center justify-center gap-2 rounded-xl bg-black text-white font-bold text-sm touch-manipulation transition-all duration-200 active:scale-[0.98] hover:bg-[#006D77] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {state.remaining <= 0 ? t('teacherCorrection.noneLeft') : t('teacherCorrection.ask')}
          </button>
          {error && <p className="mt-2 text-xs font-semibold text-red-600" role="alert">{error}</p>}
        </>
      )}

      {state.status === 'requested' && (
        <p className="text-sm text-black/65 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-[#006D77] flex-shrink-0 mt-0.5" />
          {t('teacherCorrection.requested')}
        </p>
      )}

      {state.status === 'done' && (
        <div className="space-y-3">
          <div>
            <p className="text-[11px] font-black uppercase tracking-wider text-black/45 mb-1">{t('teacherCorrection.feedback')}</p>
            <p className="text-sm text-black/80 whitespace-pre-line leading-relaxed">{state.request.teacherFeedback}</p>
          </div>
          {state.request.correctedText && (
            <div>
              <p className="text-[11px] font-black uppercase tracking-wider text-black/45 mb-1">{t('teacherCorrection.corrected')}</p>
              <p className="text-sm text-black/80 whitespace-pre-line leading-relaxed font-serif">{state.request.correctedText}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AskTeacherPanel;
